import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  VStack,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Alert,
  AlertIcon,
  useToast,
  IconButton,
  Flex,
  useColorModeValue
} from '@chakra-ui/react';
import { Camera, CameraOff, RotateCcw, Flashlight, FlashlightOff } from 'lucide-react';

interface QRScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (data: string) => void;
  onError?: (error: string) => void;
  title?: string;
  description?: string;
}

interface QRScannerState {
  scanning: boolean;
  error: string | null;
  hasCamera: boolean;
  facingMode: 'user' | 'environment';
  torchEnabled: boolean;
}

const QRScanner: React.FC<QRScannerProps> = ({
  isOpen,
  onClose,
  onScan,
  onError,
  title,
  description
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('qr.scanQR');
  const resolvedDescription = description ?? t('qr.scanDescription');
  const [state, setState] = useState<QRScannerState>({
    scanning: false,
    error: null,
    hasCamera: false,
    facingMode: 'environment',
    torchEnabled: false
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number>();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (isOpen) {
      checkCameraSupport();
    } else {
      stopScanning();
    }

    return () => {
      stopScanning();
    };
  }, [isOpen]);

  const checkCameraSupport = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      if (videoDevices.length === 0) {
        setState(prev => ({ ...prev, error: t('qr.noCamera'), hasCamera: false }));
        return;
      }

      setState(prev => ({ ...prev, hasCamera: true, error: null }));
      startScanning();
    } catch (error) {
      setState(prev => ({ ...prev, error: t('qr.cameraNotSupported'), hasCamera: false }));
    }
  };

  const startScanning = async () => {
    try {
      const constraints = {
        video: {
          facingMode: state.facingMode,
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setState(prev => ({ ...prev, scanning: true, error: null }));
          scanForQRCode();
        };
      }
    } catch (error: any) {
      const errorMsg = error.name === 'NotAllowedError'
        ? t('qr.cameraDenied')
        : t('qr.cameraFailed');
      
      setState(prev => ({ ...prev, error: errorMsg, scanning: false }));
      onError?.(errorMsg);
    }
  };

  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    setState(prev => ({ ...prev, scanning: false }));
  };

  const scanForQRCode = () => {
    if (!videoRef.current || !canvasRef.current || !state.scanning) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context || video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationRef.current = requestAnimationFrame(scanForQRCode);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    try {
      // Use a simple QR detection approach
      // In a real implementation, you'd use a proper QR library like jsQR
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const qrResult = detectQRCode(imageData);
      
      if (qrResult) {
        handleScanResult(qrResult);
        return;
      }
    } catch (error) {
      console.warn('QR scan error:', error);
    }

    animationRef.current = requestAnimationFrame(scanForQRCode);
  };

  // Simplified QR detection (in production, use jsQR or similar library)
  const detectQRCode = (imageData: ImageData): string | null => {
    // This is a placeholder - in real implementation, use jsQR
    // For demo purposes, we'll simulate QR detection with pattern matching
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;

    // Look for high contrast patterns that might indicate QR codes
    // This is highly simplified and would need proper QR detection library
    
    // For demo: detect if user types 'QR:' followed by data in console or testing
    const testData = sessionStorage.getItem('demo_qr_data');
    if (testData) {
      sessionStorage.removeItem('demo_qr_data');
      return testData;
    }

    return null;
  };

  const handleScanResult = (data: string) => {
    stopScanning();
    onScan(data);
    toast({
      title: t('qr.qrScanned'),
      description: t('qr.scanSuccess'),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const toggleCamera = async () => {
    stopScanning();
    setState(prev => ({ 
      ...prev, 
      facingMode: prev.facingMode === 'user' ? 'environment' : 'user' 
    }));
    
    // Wait a moment then restart with new camera
    setTimeout(startScanning, 500);
  };

  const toggleTorch = async () => {
    if (!streamRef.current) return;

    try {
      const track = streamRef.current.getVideoTracks()[0];
      const capabilities = track.getCapabilities() as any;
      
      if (capabilities.torch) {
        await track.applyConstraints({
          advanced: [{ torch: !state.torchEnabled } as any]
        });
        setState(prev => ({ ...prev, torchEnabled: !prev.torchEnabled }));
      }
    } catch (error) {
      console.warn('Torch not supported:', error);
    }
  };

  const simulateQRScan = (demoData: string) => {
    // For demo purposes - simulate scanning different types of QR codes
    sessionStorage.setItem('demo_qr_data', demoData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg={cardBg} m={0} borderRadius={0}>
        <ModalHeader textAlign="center" bg="black" color="white">
          {resolvedTitle}
          <ModalCloseButton color="white" />
        </ModalHeader>
        
        <ModalBody p={0} bg="black" position="relative">
          <VStack spacing={0} height="100%">
            {/* Description */}
            <Box bg="black" color="white" p={4} textAlign="center" w="100%">
              <Text fontSize="sm">{resolvedDescription}</Text>
            </Box>

            {/* Camera View */}
            <Box position="relative" flex={1} w="100%" bg="black">
              {state.hasCamera && !state.error ? (
                <>
                  <video
                    ref={videoRef}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    playsInline
                    muted
                  />
                  
                  {/* Scanning Frame Overlay */}
                  <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    w="250px"
                    h="250px"
                    border="3px solid"
                    borderColor="blue.400"
                    borderRadius="lg"
                    _before={{
                      content: '""',
                      position: 'absolute',
                      top: '-3px',
                      left: '-3px',
                      right: '-3px',
                      bottom: '-3px',
                      border: '3px solid',
                      borderColor: 'transparent',
                      borderTopColor: 'blue.400',
                      borderRadius: 'lg',
                      animation: 'spin 2s linear infinite'
                    }}
                  />

                  {/* Corner markers */}
                  {[
                    { top: '20%', left: '20%' },
                    { top: '20%', right: '20%' },
                    { bottom: '20%', left: '20%' },
                    { bottom: '20%', right: '20%' }
                  ].map((position, index) => (
                    <Box
                      key={index}
                      position="absolute"
                      w="40px"
                      h="40px"
                      borderColor="blue.400"
                      borderWidth="4px"
                      {...position}
                      _before={{
                        content: '""',
                        position: 'absolute',
                        w: '20px',
                        h: '20px',
                        borderColor: 'blue.400',
                        ...(position.top && position.left ? { borderWidth: '4px 0 0 4px', top: 0, left: 0 } :
                           position.top && position.right ? { borderWidth: '4px 4px 0 0', top: 0, right: 0 } :
                           position.bottom && position.left ? { borderWidth: '0 0 4px 4px', bottom: 0, left: 0 } :
                           { borderWidth: '0 4px 4px 0', bottom: 0, right: 0 })
                      }}
                    />
                  ))}

                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                </>
              ) : (
                <Flex align="center" justify="center" height="100%" color="white">
                  <VStack spacing={4}>
                    <Camera size={64} />
                    <Text textAlign="center">
                      {state.error || t('qr.preparingCamera')}
                    </Text>
                    {state.error && (
                      <Button onClick={checkCameraSupport} colorScheme="blue" size="sm">
                        {t('qr.retry')}
                      </Button>
                    )}
                  </VStack>
                </Flex>
              )}
            </Box>

            {/* Controls */}
            <Box bg="black" p={4} w="100%">
              <HStack justify="center" spacing={6}>
                <IconButton
                  aria-label={t('qr.toggleCamera')}
                  icon={<RotateCcw />}
                  onClick={toggleCamera}
                  isDisabled={!state.scanning}
                  colorScheme="blue"
                  variant="outline"
                  color="white"
                  borderColor="blue.400"
                />
                
                <IconButton
                  aria-label={state.torchEnabled ? t('qr.turnOffFlash') : t('qr.turnOnFlash')}
                  icon={state.torchEnabled ? <FlashlightOff /> : <Flashlight />}
                  onClick={toggleTorch}
                  isDisabled={!state.scanning}
                  colorScheme="blue"
                  variant="outline"
                  color="white"
                  borderColor="blue.400"
                />
              </HStack>

              {/* Demo Buttons */}
              <VStack spacing={2} mt={4}>
                <Text color="gray.400" fontSize="xs">{t('qr.demoQRCodes')}</Text>
                <HStack spacing={2} flexWrap="wrap" justify="center">
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="blue"
                    onClick={() => simulateQRScan('HOTEL:DEMO:CH0044')}
                  >
                    {t('qr.demoHotel')}
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="green"
                    onClick={() => simulateQRScan('MENU:RESTAURANT:1')}
                  >
                    {t('qr.demoMenu')}
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="purple"
                    onClick={() => simulateQRScan('WIFI:HOTEL-GUEST:password123')}
                  >
                    {t('qr.demoWifi')}
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default QRScanner;